import type { RefObject } from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/resource';
import type { HeadingProps } from '@atlaskit/heading';
import { type AgentsConfig } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';
import type { BrowserContextSanitizer } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import { type ActionContext } from '@atlassian/conversation-assistant-message-actions-api/types';
import { type AssistanceServiceProduct } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { UrlValidationConfig } from '@atlassian/conversation-assistant-ui-components/types';
import {
	type ConversationAction,
	type ConversationActionTags,
} from '@atlassian/rovo-conversation-actions/types';

import { type DeepPartial } from './common/types';

export type ProfileData = {
	aaid?: string;
	name: string;
	displayName?: string;
	avatarUrl?: string;
	email?: string;
};

export type ConversationAssistantConfig = {
	assistanceServiceConfig?: {
		baseUrl?: string;
		headers: Record<string, string>;
		minionAlias?: string;
		channelId?: string;
	};
	displaySubmitFlag?: boolean;
	orgId?: string;
	siteId: string;
	userId?: string;
	product: AssistanceServiceProduct;
	emojiProvider?: Promise<EmojiProvider>;
	/**
	 * If true, errors will be re-thrown after being handled by the error boundary
	 * @default false
	 */
	rethrowErrors?: boolean;
	/**
	 * If true, abort requests will not be called when the preview modal unmounts
	 * This is used to prevent streaming from cancelling while other conversation assistants are still visible
	 * @default false
	 */
	preventAbortRequestsOnStagingModalClose?: boolean;
	/**
	 * If true, abort requests will not be called when the standalone (i.e. non-modal) chat is closed
	 * This is used to prevent streaming from cancelling in case the user quickly reloads the chat window (i.e. by closing and opening the rovo sidebar)
	 * @default false
	 */
	preventAbortRequestsOnStandaloneChatClose?: boolean;
	/** Only pass for BITBUCKET and TRELLO */
	workspaceId?: string;
	experienceId?: string;
	renderingTouchpoint?: ActionContext['renderingTouchpoint'];
	/**
	 * Optional context about the current page/content the user is viewing.
	 * Used by the smart creation flow to enable "Add to page" functionality
	 * when the ephemeral preview is opened from the Rovo Chat preview card.
	 * Should be set by the host application (e.g. Confluence) when the chat is
	 * mounted on a content page.
	 */
	smartCreationContext?: {
		confluence?: {
			content?: {
				id: string;
				type: string;
				subType?: 'live' | null;
			};
		};
		/**
		 * Generic context for non-Confluence products (e.g. Jira).
		 * Provide both fields so the finalize area can route the insert command
		 * to the correct editor instance AND send the right container ARI to the
		 * duplicate remix API.
		 */
		editor?: {
			/**
			 * Pre-formed container ARI for the duplicate remix API call
			 * (e.g. `ari:cloud:jira:<cloudId>:issue/<issueId>`).
			 */
			containerAri: string;
			/**
			 * Editor instance UUID (as registered with the rovo content bridge via
			 * `getConfluenceEditorClientId`). Used to route `EditorInsertContentCommand`
			 * to the correct editor when `confluence.content.id` is not set.
			 */
			editorId: string;
		};
	};
	/**
	 * Optional attribute to identify the product surface location where Chat & Search is engaged from.
	 * Used for analytics to distinguish traffic from different Home surfaces.
	 */
	inProductExperience?: string;
};

export type UIConfig = DeepPartial<{
	externalEmbed: boolean;
	fullScreen: boolean;
	/**
	 * When true and `fullScreen` is true, renders the conversation assistant as a
	 * left/right split layout: the chat lives on the left in a resizable column,
	 * and a right-hand panel renders the chat result "Sources" + "Output" artefacts
	 * @default false
	 */
	isSplitLayout: boolean;
	/**
	 * Initial width (px) of the **right** panel when `isSplitLayout` is enabled.
	 * The chat (left) fills the remaining horizontal space, so a smaller value
	 * here makes the chat visually dominant.
	 * @default 380
	 */
	splitLayoutInitialWidth: number;
	/**
	 * Minimum width (px) of the left (chat) panel when `isSplitLayout` is enabled.
	 * The chat is always at least this wide.
	 * @default 480
	 */
	splitLayoutMinWidth: number;
	/**
	 * Minimum width (px) of the right panel when `isSplitLayout` is enabled.
	 * @default 280
	 */
	splitLayoutAdjacentMinWidth: number;
	/**
	 * `localStorage` key used to persist the right panel's width across reloads
	 * when `isSplitLayout` is enabled. Set to an empty string (`''`) to opt out
	 * of persistence entirely.
	 *
	 * Use distinct keys per surface (e.g. `'jira-agent-sessions.split-width'`)
	 * if you want the persisted width to be independent of other consumers.
	 * @default 'conversation-assistant.split-layout.right-width'
	 */
	splitLayoutStorageKey: string;
	isBitbucketBetaEnabled: boolean;
	customFeedbackCategories?: {
		positive: readonly string[];
		negative: readonly string[];
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	stagingAreaRef: any;
	/**
	 * If true, enables the context banner to follow browser context from external sites.
	 * When enabled, the context banner will display browser context when available.
	 * @default false
	 */
	enableBrowserContextBanner?: boolean;
	/**
	 * If true, enables content preview support in the smart creation modal.
	 * This should be set to true for all products using smart create modal or
	 * staging area ref.
	 * @default true
	 */
	isContentPreviewSupported?: boolean;
	/**
	 * When true, fullscreen chat does not update `document.title` with the agent/conversation title.
	 * @default false
	 */
	hideBrowserTabTitle?: boolean;
	/**
	 * When true, fullscreen chat does not replace the browser tab favicon with the selected agent icon.
	 * @default false
	 */
	hideBrowserTabFavicon?: boolean;
	/**
	 * When true, the Rovo settings modal is disabled and all triggers
	 * (app filter, header settings, memory spotlight) become no-ops.
	 * Use this for constrained viewports such as the Chrome extension sidebar
	 * where the large modal cannot render properly.
	 * @default false
	 */
	disableSettingsModal?: boolean;
	elements: {
		closeButton: boolean;
		newChatButton: boolean;
		fullscreenButton: boolean;
		createAgentButton: boolean;
		browseAgentsButton: boolean;
		agentsMenu: boolean;
		navbar: boolean;
		mainChatHeader: boolean;
		stagingAreaEnabled: boolean;
		voiceDictationButton: boolean;
		chatModeMenuButton: boolean;
		fileUploadButton: boolean;
		forceFullscreenHeader: boolean;
		/**
		 * Controls whether the greeting is displayed in the empty state.
		 * When true, the greeting is shown
		 * When false, the greeting is always hidden regardless of mode.
		 * When undefined, the greeting is shown in all modes except mini modal, where it is hidden. This is to accommodate the limited space in mini modal.
		 * @default undefined
		 */
		shouldShowGreeting?: boolean;
	};
}> & {
	/**
	 * Optional custom greeting configuration. When provided, a CustomGreetingCard will be
	 * rendered with the given icon and title instead of the default RovoGreeting.
	 */
	customGreeting?: {
		/** The icon source URL for light theme. Falls back to default icon if not provided */
		iconSrc?: string;
		/** The icon source URL for dark theme. Falls back to `iconSrc` if not provided. */
		iconSrcDark?: string;
		/** The greeting title text. Limited to 50 characters. Falls back to default greeting text if not provided */
		title?: string;
		/**
		 * Optional secondary text rendered directly below the title at a smaller,
		 * subdued style. Useful for a short call-to-action or onboarding context
		 * line.
		 */
		subtitle?: string;
		/** Optional test ID. Will be prefixed with 'custom-rovo-greeting-'. */
		testId?: string;
		/** Duration of the fade-in animation. @default 'large' */
		animationDuration?: 'small' | 'medium' | 'large';
		/** Direction the greeting enters from. @default 'bottom' */
		entranceDirection?: 'top' | 'bottom' | 'left' | 'right';
		/** Direction the greeting exits to. @default 'top' */
		exitDirection?: 'top' | 'bottom' | 'left' | 'right';
		/** Key for the FadeIn animation to control mount/unmount transitions. @default 'custom-greeting' */
		animationKey?: string;
		/** Allow consumer to override the greeting truncation logic */
		truncateGreeting?: boolean;
		/** Size of the title heading. Accepts any value supported by the Heading component's size prop. @default 'xlarge' */
		titleSize?: HeadingProps['size'];
	};
	customHandlers?: {
		handleSubmitSmartCreate?: ({ baseUrl, path }: { baseUrl: string; path: string }) => void;
		handleCloseSmartCreate?: ({
			currentConversationId,
		}: {
			currentConversationId?: string | null;
		}) => void;
		/**
		 * Callback that allows the host application to handle a specialized deep link for SPA routing.
		 * deep links are identified by the presence of a `deepLinkTarget` query parameter in the URL of clicked links.
		 * Ex of url sent to callback: https://example.atlassian.net/some-path?deepLinkTarget=jira#section
		 * Use in conjunction with the config.product prop and its internally mapped ProductKey in packages/ai-mate/conversation-assistant/src/common/utils/global-config/index.ts
		 * to enable deep links to be handled by the host application.
		 */
		handleDeepLinkClick?: (url: string) => void;
		/**
		 * Optional onClick handler when the user chooses "Browse agents" in the header agent dropdown.
		 * If not supplied, the default behavior is to open the browse agents modal within the conversation assistant.
		 */
		onBrowseAgentClick?: () => void;
	};
	/**
	 * Optional sanitizer for browser context URLs. When provided, URLs will be checked
	 * against the sanitizer's blocklist before being included in browser context.
	 */
	browserContextSanitizer?: BrowserContextSanitizer;
	promptInputDroppableUrls?: UrlValidationConfig;
};

export interface AgentVersioningConfig {
	/**
	 * Shows version picker in agent empty state.
	 * Only displayed for users with admin/collaborator permission to the agent.
	 * @default false
	 */
	showVersionPicker?: boolean;

	/**
	 * Preserves selected version when creating new chat from current conversation.
	 * When false/undefined, new chats default to LIVE version.
	 * @default false
	 */
	preserveVersionOnNewChat?: boolean;
}

export type ConversationAssistantModeConfig = {
	/**
	 * When provided, overrides the default UI mode.
	 * This allows consumers to use render the conversation assistant in mini modal mode.
	 * @default ConversationAssistantUIModes.SIDEBAR
	 */
	mode: ConversationAssistantUIModes;

	/**
	 * Indicates whether mini modal mode is available to be switched to in the consumer's context.
	 * Leverage the @atlassian/rovo-experience-api for this information
	 * This allows consumers to conditionally enable or disable mini modal mode.
	 */
	isMiniModalAvailable: boolean;

	/**
	 * Indicates whether sidebar mode is available to be switched to in the consumer's context.
	 * Leverage the @atlassian/rovo-experience-api for this information
	 * This allows consumers to conditionally enable or disable sidebar mode.
	 * If not provided, it defaults to true.
	 */
	isSidebarAvailable?: boolean;

	/**
	 * A callback, that enables the mode switching intent triggering from within conversation assistant,
	 * the actual switching happens outside this component, if the intention is to change mode of the same instance component,
	 * change in mode prop is warranted from consumer in response to this callback.
	 */
	switchToMode: (mode: ConversationAssistantUIModes | undefined) => void;
};

export interface ConversationAssistantProps {
	/**
	 * A `testId` prop is provided for specified elements, which is a unique
	 * string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests */
	testId?: string;

	/**
	 * Callback used to display a "close" button and trigger the action provided by the consumer.
	 */
	onCloseChat?: () => void;

	/**
	 * Provides credentials and other settings
	 */
	config: ConversationAssistantConfig;

	/**
	 * Provides configurable UI settings so integrators can customize the appearance and behavior suitable for their context.
	 */
	uiConfig?: Partial<UIConfig>;

	/**
	 * Provides an option to configure the agents.
	 */
	agentsConfig?: AgentsConfig;

	/**
	 * Configuration for agent versioning features.
	 * When provided, enables versioning-related UI and behaviors.
	 * Typically used in Studio context.
	 */
	agentVersioningConfig?: AgentVersioningConfig;

	/**
	 * Provides a function to render a custom site picker.
	 */
	sitePickerRender?: JSX.Element;

	/**
	 * When provided, replaces the default conversation assistant UI with custom React content.
	 * This allows consumers to initialize the chat state when the side panel chat is not rendered yet.
	 */
	overridingChildren?: React.ReactNode;

	/**
	 * When true, uses the overriding children as the fallback component when the conversation assistant is loading.
	 * @default false
	 */
	useChildrenAsLoadingFallback?: boolean;

	/**
	 * When provided, overrides the default mode and switchToMode configuration.
	 * This allows consumers to render the conversation assistant in mini modal mode and switch between modes.
	 * By default, the conversation assistant will be rendered in sidebar mode without ability to switch modes
	 * if this prop is not provided.
	 */
	modeConfig?: ConversationAssistantModeConfig;

	/**
	 * An async callback that returns the actions to be displayed in the conversation assistant.
	 * This is used to override the actions that are displayed in the conversation assistant.
	 */
	getActions?: () => Promise<ConversationAction[]>;

	/**
	 * When true, only the actions fetched from the custom getActions provider are displayed,
	 * rather than being merged with the default actions (dynamic starters, prompt-of-day, etc).
	 *
	 * Note: if setEmptyChatLaunch is called with replaceProviderActions=true at runtime,
	 * the store's actions will take precedence over getActions entirely — this prop then
	 * controls display behaviour (suppressing dynamic starters) for those store actions.
	 * @default false
	 */
	prioritizeCustomActionResults?: boolean;

	/**
	 * The number of conversation starters to show.
	 * @default 3
	 */
	numStartersToShow?: number;

	/**
	 * The vertical offset (in pixels) from the top of the page layout. This value is used to adjust the mini modal's
	 * max height, to prevent it from overlapping with any components at the top of the page (like the navbar).
	 */
	topOffset?: number;
	/**
	 * Tags configuration for skill metadata fetching.
	 * When provided, these tags are used to fetch relevant skill metadata from the backend
	 * via RovoSkillsConfigProvider context.
	 */
	tags?: ConversationActionTags;

	/**
	 * When provided, the conversation assistant will automatically open a new chat
	 * and send this prompt as the first message. Used by the embedded Confluence editor
	 * to trigger Rovo chat with a pre-filled prompt from a Jira nudge.
	 */
	initialPrompt?: string;
}

/* ConversationAssistantUIModes is duplicated in rovo-experience-api-store package
   to avoid circular dependencies. Any changes here should be mirrored in
	 platform/packages/ai-mate/rovo-experience-api/src/services/rovo-experience-api-store/types.ts
*/
export const ConversationAssistantUIModes = {
	SIDEBAR: 'sidebar',
	MINI_MODAL: 'mini-modal',
} as const;

export type ConversationAssistantUIModes =
	(typeof ConversationAssistantUIModes)[keyof typeof ConversationAssistantUIModes];

export interface ConversationAssistantInternalProps {
	testId?: string;
	sitePickerRender?: JSX.Element;
	stagingAreaRef?: RefObject<HTMLDivElement>;
}
